import { HostAriaLabelMixin, SchmancyFormField } from '@schmancy/mixins/index'
import { css, html, type PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { when } from 'lit/directives/when.js'
import { from, switchMap, takeUntil, tap } from 'rxjs'
import '@schmancy/primitives/typography/typography'

export type SchmancyEditorChangeEvent = CustomEvent<{ value: string }>

/** The engine's own surface, kept to what this component actually uses so the dynamic import stays honest about its shape. */
type Engine = {
	view: typeof import('@codemirror/view')
	state: typeof import('@codemirror/state')
	language: typeof import('@codemirror/language')
	markdown: typeof import('@codemirror/lang-markdown')
	commands: typeof import('@codemirror/commands')
	highlight: typeof import('@lezer/highlight')
}

/**
 * @element schmancy-editor
 *
 * Markdown editor. `value` in and `change` out are both the markdown source,
 * byte-identical: the document the user edits IS the stored string, so there
 * is no conversion boundary that could rewrite a stored value a little on
 * every save cycle, and no HTML anywhere to sanitize.
 *
 * The engine (CodeMirror) is ~170 kB gzipped and is dynamically imported on
 * connect, never entering a consumer's synchronous boot graph. Until it
 * resolves the field IS a real `<textarea>` — same box, same value, same form
 * participation — so there is no layout jump and no keystroke is lost; the
 * caret position carries across the swap.
 *
 * @fires change - `{ value: string }` on every edit.
 */
@customElement('schmancy-editor')
export class SchmancyEditor extends HostAriaLabelMixin(
	SchmancyFormField(css`
		:host {
			display: block;
			--editor-border: var(--schmancy-sys-control-border);
		}
		/* The engine's own DOM carries no class attribute a template could set, so
	   its border colour arrives as an inherited custom property the theme reads
	   — which keeps every rule here a :host rule. */
		:host(:focus-within) {
			--editor-border: var(--schmancy-sys-color-secondary-default);
		}
		:host([error]) {
			--editor-border: var(--schmancy-sys-color-error-default);
		}
	`),
) {
	// name, label, required, disabled, readonly, error, validationMessage,
	// hint, validateOn, touched/dirty/submitted, checkValidity, resetForm,
	// emitChange and the FIELD_CONNECT_EVENT handshake all come from
	// SchmancyFormField. `value` stays the mixin's own string property —
	// this field's value model is exactly what the mixin already assumes.

	@property({ type: String }) placeholder = ''

	/** Minimum height of the writing surface. A CSS length; the editor grows past it with content. */
	@property({ type: String, attribute: 'min-height' }) minHeight = '10rem'

	@state() private engineReady = false

	private readonly host = createRef<HTMLDivElement>()
	private readonly fallback = createRef<HTMLTextAreaElement>()
	private editor?: import('@codemirror/view').EditorView

	override connectedCallback(): void {
		super.connectedCallback()

		from(
			Promise.all([
				import('@codemirror/view'),
				import('@codemirror/state'),
				import('@codemirror/language'),
				import('@codemirror/lang-markdown'),
				import('@codemirror/commands'),
				import('@lezer/highlight'),
			]).then(
				([view, state, language, markdown, commands, highlight]): Engine => ({
					view,
					state,
					language,
					markdown,
					commands,
					highlight,
				}),
			),
		)
			.pipe(
				// The container only exists after the first render, and the engine
				// import can resolve before it — wait for the render this component
				// has already scheduled rather than mounting into nothing.
				switchMap(engine => from(this.updateComplete).pipe(tap(() => this.mount(engine)))),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	override disconnectedCallback(): void {
		super.disconnectedCallback()
		this.editor?.destroy()
		this.editor = undefined
	}

	/**
	 * Two callers — the engine pipeline above and `updated()`, which remounts
	 * when `readonly`/`disabled` flips, because those are `EditorState`
	 * configuration rather than DOM attributes the engine re-reads.
	 */
	private mount(engine: Engine): void {
		const container = this.host.value
		if (!container) return

		const { EditorView, keymap, placeholder } = engine.view
		const { EditorState, Prec } = engine.state
		const { syntaxHighlighting, HighlightStyle } = engine.language
		const { tags } = engine.highlight

		// The caret is carried across the swap so a user typing into the fallback
		// while the engine loads is not thrown back to position 0.
		const caret =
			this.fallback.value && this.shadowRoot?.activeElement === this.fallback.value
				? this.fallback.value.selectionStart
				: null

		this.editor?.destroy()
		this.editor = new EditorView({
			parent: container,
			// The engine resolves selection and focus against this root. Declared
			// rather than left to be derived from `parent`, so it stays correct if
			// this host is ever reparented across shadow roots.
			root: this.shadowRoot ?? document,
			state: EditorState.create({
				doc: this.value == null ? '' : String(this.value),
				selection: caret === null ? undefined : { anchor: Math.min(caret, String(this.value ?? '').length) },
				extensions: [
					engine.markdown.markdown(),
					EditorView.lineWrapping,
					engine.commands.history(),
					placeholder(this.placeholder),
					syntaxHighlighting(
						HighlightStyle.define([
							{ tag: tags.strong, fontWeight: '700' },
							{ tag: tags.emphasis, fontStyle: 'italic' },
							{ tag: tags.heading, fontWeight: '700' },
							{ tag: tags.monospace, fontFamily: 'ui-monospace, monospace' },
							{ tag: tags.link, textDecoration: 'underline' },
							{ tag: tags.url, color: 'var(--schmancy-sys-color-primary-default)' },
							{ tag: tags.list, color: 'var(--schmancy-sys-color-surface-onVariant)' },
							{ tag: tags.processingInstruction, color: 'var(--schmancy-sys-color-surface-onVariant)' },
						]),
					),
					// Highest precedence so these win over defaultKeymap, which binds
					// Mod-Enter to insertBlankLine. Returning true suppresses that
					// default; the native keydown still bubbles out of the shadow root
					// to the host form, because a handled binding preventDefaults but
					// never stops propagation.
					Prec.highest(
						keymap.of([
							{ key: 'Mod-Enter', run: () => true },
							{ key: 'Mod-b', run: view => wrapSelection(view, '**') },
							{ key: 'Mod-i', run: view => wrapSelection(view, '_') },
							{
								key: 'Mod-k',
								run: view => {
									const { from: start, to } = view.state.selection.main
									view.dispatch({
										changes: [
											{ from: start, insert: '[' },
											{ from: to, insert: '](url)' },
										],
										selection: { anchor: to + 3, head: to + 6 },
									})
									return true
								},
							},
						]),
					),
					keymap.of(engine.commands.defaultKeymap.concat(engine.commands.historyKeymap)),
					EditorView.editable.of(!this.disabled && !this.readonly),
					EditorState.readOnly.of(this.disabled || this.readonly),
					EditorView.contentAttributes.of(() => ({ 'aria-label': this.ariaLabel || this.label || 'Editor' })),
					EditorView.theme({
						'&': {
							fontSize: 'inherit',
							fontFamily: 'inherit',
							border: '1px solid var(--editor-border)',
							borderRadius: 'var(--schmancy-radius-xl, 0.875rem)',
							background: 'var(--schmancy-sys-color-surface-containerLowest)',
							color: 'var(--schmancy-sys-color-surface-on)',
							overflow: 'hidden',
						},
						'.cm-content': {
							padding: '0.75rem',
							minHeight: this.minHeight,
							caretColor: 'var(--schmancy-sys-color-surface-on)',
						},
						'.cm-cursor, .cm-dropCursor': { borderLeftColor: 'var(--schmancy-sys-color-surface-on)' },
						'.cm-placeholder': { color: 'var(--schmancy-sys-color-surface-onVariant)' },
						'.cm-selectionBackground, &.cm-focused .cm-selectionBackground, ::selection': {
							background: 'var(--schmancy-sys-color-secondary-container)',
						},
						'.cm-activeLine': { backgroundColor: 'transparent' },
						'&.cm-focused': { outline: 'none' },
						'.cm-scroller': { fontFamily: 'inherit', lineHeight: '1.6' },
					}),
					EditorView.updateListener.of(update => {
						if (!update.docChanged) return
						this.value = update.state.doc.toString()
						this.markTouched()
						this.emitChange({ value: this.value })
					}),
				],
			}),
		})

		this.engineReady = true
		if (caret !== null) this.editor.focus()
	}

	/** Focus the writing surface — the engine's own content element once mounted, the fallback until then. */
	override focus(options?: FocusOptions): void {
		if (this.editor) {
			this.editor.focus()
			return
		}
		this.fallback.value?.focus(options)
	}

	protected override updated(changed: PropertyValues): void {
		super.updated(changed)
		if (!this.editor) return
		// CodeMirror owns its content DOM; re-evaluate its attribute facet when
		// the host name changes without replacing the focused writing surface.
		if (changed.has('ariaLabel') || changed.has('label')) this.editor.dispatch({})

		// A consumer's `.value=` write lands in the document without echoing a
		// `change` back: the updateListener only fires on a real doc change, and
		// this guard keeps a programmatic set from being replayed as user intent.
		if (changed.has('value')) {
			const next = this.value == null ? '' : String(this.value)
			if (next !== this.editor.state.doc.toString()) {
				this.editor.dispatch({ changes: { from: 0, to: this.editor.state.doc.length, insert: next } })
			}
		}
	}

	override render() {
		return html`
			<div class="grid gap-1">
				${when(
					!!this.label,
					() => html`<schmancy-typography type="label" token="sm">${this.label}</schmancy-typography>`,
				)}

				<div ${ref(this.host)} class=${this.classMap({ hidden: !this.engineReady })}></div>

				${when(
					!this.engineReady,
					() => html`
						<textarea
							${ref(this.fallback)}
							class="w-full min-w-0 rounded-xl border border-control-border bg-surface-containerLowest p-3 text-surface-on outline-secondary-default focus:outline-1"
							style="min-height: ${this.minHeight}"
							aria-label=${this.ariaLabel || this.label || 'Editor'}
							placeholder=${this.placeholder}
							?disabled=${this.disabled}
							?readonly=${this.readonly}
							.value=${this.value == null ? '' : String(this.value)}
							@input=${(e: Event) => {
								this.value = (e.target as HTMLTextAreaElement).value
								this.markTouched()
								this.emitChange({ value: this.value })
							}}
						></textarea>
					`,
				)}
				${when(
					this.error && !!this.validationMessage,
					() =>
						html`<schmancy-typography role="alert" type="body" token="xs" class="wrap-anywhere text-error-default"
							>${this.validationMessage}</schmancy-typography
						>`,
				)}
				${when(
					!!this.hint && !this.error,
					() =>
						html`<schmancy-typography type="body" token="xs" class="wrap-anywhere text-surface-onVariant"
							>${this.hint}</schmancy-typography
						>`,
				)}
			</div>
		`
	}
}

/** Two callers (Mod-b, Mod-i): wraps the selection in a marker, or unwraps it when the marker is already there. */
function wrapSelection(view: import('@codemirror/view').EditorView, marker: string): boolean {
	const { from: start, to } = view.state.selection.main
	const width = marker.length
	const wrapped =
		start - width >= 0 &&
		to + width <= view.state.doc.length &&
		view.state.doc.sliceString(start - width, start) === marker &&
		view.state.doc.sliceString(to, to + width) === marker

	if (wrapped) {
		view.dispatch({
			changes: [
				{ from: start - width, to: start },
				{ from: to, to: to + width },
			],
			selection: { anchor: start - width, head: to - width },
		})
		return true
	}
	view.dispatch({
		changes: [
			{ from: start, insert: marker },
			{ from: to, insert: marker },
		],
		selection: { anchor: start + width, head: to + width },
	})
	return true
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-editor': SchmancyEditor
	}
}
